import { lazy, Suspense, useState, useCallback } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import CountdownBanner from "@/components/CountdownBanner";
import { SEO, OrganizationJsonLd } from "@/components/SEO";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  FeaturedProductsSkeleton,
  CategoriesSkeleton,
  InstagramFeedSkeleton,
  NewsletterSkeleton,
} from "@/components/HomepageSkeletons";

// Lazy load all below-the-fold components to improve TTI
const FeaturedProducts = lazy(() => import("@/components/FeaturedProducts"));
const Categories = lazy(() => import("@/components/Categories"));
const InstagramFeed = lazy(() => import("@/components/InstagramFeed"));
const Newsletter = lazy(() => import("@/components/Newsletter"));
const Footer = lazy(() => import("@/components/Footer"));

const Index = () => {
  const [prefsOpen, setPrefsOpen] = useState(false);
  const handlePrefsChange = useCallback((open: boolean) => setPrefsOpen(open), []);

  return (
    <div className="min-h-screen bg-background">
      <SEO url="/" />
      <OrganizationJsonLd />
      <Header onPrefsChange={handlePrefsChange} />
      <div className={`fixed left-0 right-0 z-[39] transition-all duration-300 ease-out ${
        prefsOpen ? "top-[calc(4rem+3rem)] md:top-[calc(5rem+3rem)]" : "top-16 md:top-20"
      }`}>
        <CountdownBanner />
      </div>
      <main>
        <ErrorBoundary>
          <Hero />
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<FeaturedProductsSkeleton />}>
            <FeaturedProducts />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<CategoriesSkeleton />}>
            <Categories />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<InstagramFeedSkeleton />}>
            <InstagramFeed />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<NewsletterSkeleton />}>
            <Newsletter />
          </Suspense>
        </ErrorBoundary>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
